import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { useDispatch } from 'react-redux';
import { handlePostRequest } from '../../services/PostTemplate';
import { handleGetRequest } from '../../services/GetTemplate';
import Loader from '../../components/loader';
import { Dropdown } from 'primereact/dropdown';
import QRCode from 'qrcode.react';
import { toast } from 'react-toastify';


function RenewFitnessCertificate({ onHideSidebar, rowData, fetchCertificatesData }) {
    const [loading, setLoading] = useState(false);
    const [vehicleTypes, setVehicleTypes] = useState([]);
    const [gracePeriod, setGracePeriod] = useState(0);
    const [fineValue, setFineValue] = useState(0);
    const [paymentMethod, setPaymenytMetod] = useState();

    const dispatch = useDispatch();

    const calculateFeeAndFine = (vehicleType) => {
    if (!vehicleType || !rowData?.expDate) return { fineAmount: 0, totalAmount: 0 };

    const expiryDate = new Date(rowData.expDate);
    const gracePeriodDays = vehicleType.gracePeriod || 0;
    const graceDate = new Date(expiryDate);
    graceDate.setDate(graceDate.getDate() + gracePeriodDays);

    const today = new Date();
    
    // Only calculate fine if we're past the grace period
    let fineAmount = 0;
    if (today > graceDate) {
        // Calculate months past grace period (not days)
        // Get year and month difference
        let yearDiff = today.getFullYear() - graceDate.getFullYear();
        let monthDiff = today.getMonth() - graceDate.getMonth();
        
        // Calculate total months difference
        let overdueMonths = yearDiff * 12 + monthDiff;
        
        // Adjust for day of month (if today's day is earlier in the month than grace date's day)
        if (today.getDate() < graceDate.getDate()) {
            overdueMonths--;
        }
        
        // Since we're past grace period, ensure at least 1 month of fine applies
        // This is important because we want to charge at least one month even if 
        // it's just one day past the grace period
        overdueMonths = Math.max(1, overdueMonths);
        
        // Apply fine per month using the Vehicle Category's fine amount
        fineAmount = vehicleType.fine * overdueMonths;
    }
    
    const totalAmount = vehicleType.renewalFee + fineAmount;

    return { fineAmount, totalAmount };
};
    const formik = useFormik({
        initialValues: {
            cfxNo: rowData?.cfxNo || '',
            vehicleNo: rowData?.vehicleNo || '',
            typeOfVehicle: rowData?.vehicleTypeid || '',
            expiryDate: rowData?.expDate ? new Date(rowData.expDate) : null,
            renewalFee: 0,
            fineAmount: 0,
            totalAmount: 0,
            paymentMethod: ''
        },
        validationSchema: Yup.object().shape({
            cfxNo: Yup.string().required('CFX No is required'),
            vehicleCategory: Yup.string().required('Vehicle Category is required'),
            renewalFee: Yup.number().required('Renewal fee is required').min(0, 'Fee cannot be negative'),
            fineAmount: Yup.number().min(0, 'Fine cannot be negative'),
            totalAmount: Yup.number().required('Total amount is required').min(0, 'Total amount cannot be negative'),
            paymentMethod: Yup.string().required('This field is required')
        }),
        enableReinitialize: true,
        onSubmit: async (values) => {
            try {
                setLoading(true);
                const payload = {
                    cfxNo: values.cfxNo,
                    fee: values.renewalFee,
                    fineAmount: values.fineAmount,
                    totalAmount: values.totalAmount
                };
                const response = await dispatch(handlePostRequest(payload, '/api/fitness/renew', false, true));
                if (response?.success) {
                    const responseId = response?.data?.requestId;
                    if (formik.values.paymentMethod === '0') {
                        const response = await handleGetRequest(`/api/fitness/DQR/${responseId}`, true);
                        if (response?.success === true) {
                            setPaymenytMetod(response?.data);
                        }
                    } else {
                        fetchCertificatesData();
                        onHideSidebar();
                    }
                }
                if (response?.success === false) {
                    toast.error(response?.message || 'Failed to renew fitness certificate');
                }
            } catch (error) {
                console.error('Error:', error);
            } finally {
                setLoading(false);
            }
        }
    });

    useEffect(() => {
        const getVehiclesList = async () => {
            const response = await handleGetRequest('/api/fitness/fee', false);
            if (response?.success && rowData && response.data.length > 0) {
                setVehicleTypes(response.data);
                const selectedVehicleType = response.data.find((vehicle) => vehicle.id.toString() === rowData.vehicleTypeid?.toString());
                if (selectedVehicleType) {
                    const { fineAmount, totalAmount } = calculateFeeAndFine(selectedVehicleType);
                    formik.setFieldValue('renewalFee', selectedVehicleType?.renewalFee || 0);
                    formik.setFieldValue('fineAmount', fineAmount);
                    formik.setFieldValue('totalAmount', totalAmount);

                    setGracePeriod(selectedVehicleType.gracePeriod || 0);
                    setFineValue(selectedVehicleType.fine || 0);
                }
            }
        };
        getVehiclesList();
        // eslint-disable-next-line
    }, [rowData]);

    useEffect(() => {
        const renewalFee = parseFloat(formik.values.renewalFee) || 0;
        const fineAmount = parseFloat(formik.values.fineAmount) || 0;
        formik.setFieldValue('totalAmount', renewalFee + fineAmount);
        // eslint-disable-next-line
    }, [formik.values.renewalFee, formik.values.fineAmount]);

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);
    const getFormErrorMessage = (name) => isFormFieldValid(name) && <small className="p-error">{formik.errors[name]}</small>;

    const getVehicleTypeName = () => {
        const vehicle = vehicleTypes.find((v) => v.id?.toString() === rowData?.vehicleTypeid?.toString());
        return vehicle ? vehicle.categoryName : 'Unknown';
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const methodLov = [
        {
            label: 'Cash Payment',
            value: '1'
        },
        {
            label: 'Online Payment',
            value: '0'
        }
    ];

    const handleClose = () => {
         fetchCertificatesData()
         onHideSidebar();
     };

    return (
        <div className="sidebar-container" style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
            <div className="sidebar-header">
                <div className="header-content">
                    <h2>Renew Fitness Certificate</h2>
                    <Button icon="pi pi-times" onClick={onHideSidebar} className="p-button-rounded p-button-danger p-button-text p-button-sm" style={{ fontSize: '1rem' }} />
                </div>
            </div>
            {!paymentMethod && (
                <>
                    <div className="p-4" style={{ flex: '1 1 auto', overflow: 'auto' }}>
                        <form onSubmit={formik.handleSubmit}>
                            <div style={{ paddingBottom: '10px' }}>
                                <div className="form-section w-full mb-3">
                                    <div className="grid-1">
                                        <div className="form-group mb-3">
                                            <label className="form-label mb-2">CFX No.</label>
                                            <InputText id="cfxNo" name="cfxNo" value={formik.values.cfxNo} disabled onChange={formik.handleChange} className={isFormFieldValid('cfxNo') ? 'p-invalid w-full' : 'w-full'} />
                                            {getFormErrorMessage('cfxNo')}
                                        </div>

                                        <div className="form-group mb-3">
                                            <label className="form-label mb-2">Vehicle Number</label>
                                            <InputText id="vehicleNo" name="vehicleNo" value={formik.values.vehicleNo} className="w-full" disabled />
                                        </div>

                                        <div className="form-group mb-3">
                                            <label className="form-label mb-2">Vehicle Category</label>
                                            <InputText id="typeOfVehicleDisplay" value={getVehicleTypeName()} className="w-full" disabled />
                                        </div>

                                        <div className="form-group mb-3">
                                            <label className="form-label mb-2">Expiry Date</label>
                                            <InputText id="expiryDate" value={formatDate(rowData?.expDate)} className="w-full" disabled />
                                        </div>
                                    </div>
                                </div>
                                <div className="form-group mb-3">
                                    <label className="form-label mb-2">Payment Method</label>
                                    <Dropdown
                                        options={methodLov}
                                        optionValue="value"
                                        optionLabel="label"
                                        id="paymentMethod"
                                        name="paymentMethod"
                                        value={formik.values.paymentMethod}
                                        onChange={formik.handleChange}
                                        placeholder="Choose payment method"
                                        className="Dropdown__Round"
                                    />
                                    {getFormErrorMessage('paymentMethod')}
                                </div>
                                <div className="form-section w-full">
                                    <div className="grid-1">
                                        <div className="form-group mb-3">
                                            <label className="form-label mb-2">Renewal Fee</label>
                                            <InputNumber
                                                id="renewalFee"
                                                name="renewalFee"
                                                value={formik.values.renewalFee}
                                                mode="currency"
                                                currency="PKR"
                                                locale="en-US"
                                                className={isFormFieldValid('renewalFee') ? 'p-invalid w-full' : 'w-full'}
                                                disabled
                                            />
                                            {getFormErrorMessage('renewalFee')}
                                        </div>

                                        <div className="form-group mb-3">
                                            <label className="form-label mb-2">Fine Amount</label>
                                            <InputNumber
                                                id="fineAmount"
                                                name="fineAmount"
                                                value={formik.values.fineAmount}
                                                onValueChange={(e) => formik.setFieldValue('fineAmount', e.value)}
                                                mode="currency"
                                                currency="PKR"
                                                locale="en-US"
                                                className={isFormFieldValid('fineAmount') ? 'p-invalid w-full' : 'w-full'}
                                                min={0}
                                                disabled
                                            />
                                            {getFormErrorMessage('fineAmount')}
                                        </div>

                                        <div className="form-group mb-3">
                                            <label className="form-label mb-2">Total Amount</label>
                                            <InputNumber
                                                id="totalAmount"
                                                name="totalAmount"
                                                value={formik.values.totalAmount}
                                                mode="currency"
                                                currency="PKR"
                                                locale="en-US"
                                                className={isFormFieldValid('totalAmount') ? 'p-invalid w-full bg-gray-100' : 'w-full bg-gray-100'}
                                                disabled
                                            />
                                            {getFormErrorMessage('totalAmount')}
                                        </div>
                                    </div>
                                </div>

                                <div className="form-group mt-2">
                                    <p style={{ color: 'red', fontWeight: 500 }}>{`After ${gracePeriod} days of the certificate expiry date, a PKR ${fineValue} fine will be applied per month.`}</p>
                                </div>
                            </div>
                        </form>
                    </div>
                    <div className="border-top-1 border-300 pt-2" style={{ position: 'sticky', bottom: 0, background: 'white', padding: '1rem' }}>
                        <Button type="button" onClick={() => formik.handleSubmit()} icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-check'} label={loading ? 'Please wait...' : 'Renew Certificate'} className="submit-button w-full" disabled={loading} />
                    </div>
                </>
            )}
            {paymentMethod && (
                <>
                    <>
                        <div style={{ display: 'flex', justifyContent: 'center', margin: '1rem' }}>
                            <QRCode value={paymentMethod?.qrString} size={256} />
                        </div>
                        <p style={{ color: 'red', fontWeight: 500, textAlign: 'center' }}>{`Use QR Code for the payment or use consumer id: ${paymentMethod?.consumerId}`}</p>
                        <div className="mt-3 pt-3 border-top-1 border-300" style={{ position: 'sticky', bottom: 0, background: 'white' }}>
                            <Button label="Close" className="Btn__Dark w-full" onClick={handleClose} />
                        </div>
                    </>
                </>
            )}

            {loading && <Loader />}
        </div>
    );
}

export default RenewFitnessCertificate;
